import React, { useRef, useEffect } from 'react'
import { WarningIcon } from '@chakra-ui/icons'
import { Box, HStack, Text, BoxProps, TextProps } from '@chakra-ui/react'
import {
    Popover,
    PopoverTrigger,
    PopoverArrow,
    PopoverContent,
    PopoverBody,
} from '@chakra-ui/react'
import { Menu, MenuButton, MenuList, MenuProps } from '@chakra-ui/react'

import SharedLabel from './shared-label'
import { ChevronDownIcon } from '@chakra-ui/icons'
import { Button, ButtonProps } from '@chakra-ui/react'
import { MenuListProps, useDisclosure } from '@chakra-ui/react'
import GradientDivider from '../gradient-divider'
import MenuItem, { MenuItemProps } from '../menu/menu-item'
import { FaCheck } from 'react-icons/fa6'
import { VscBlank } from 'react-icons/vsc'
import SharedStack, { SharedStackProps } from '../wrappers/shared-stack'

export interface MenuSelectProps extends ButtonProps {
    label?: string
    labelProps?: TextProps
    labelEnhancer?: React.ReactElement
    moreInfo?: React.ReactElement
    /** Rendered next to the label (e.g. enterprise field-link control). */
    labelActions?: React.ReactNode
    containerProps?: BoxProps
    placeholder?: string
    textModification?: (e: string) => string
    options?: string[] | { value: string; rightElement?: React.ReactElement }[]
    renderValue?: (value: string) => React.ReactNode
    renderOption?: (option: string, index?: number) => React.ReactNode
    value?: string
    setValue?: (value: string, index?: number) => void
    listMaxHeight?: string
    listHeader?: React.ReactNode
    listOverrides?: Omit<MenuListProps, 'maxH' | 'overflowY'>
    menuOverrides?: Omit<MenuProps, 'isOpen' | 'onClose' | 'children'>
    divideIndices?: number[]
    error?: React.ReactNode
    hideDropdownIcon?: boolean
    stackOverrides?: SharedStackProps
    menuItemOverrides?: MenuItemProps
}

/**
 * Having weird scroll issues?  We've got a bug with renderOption where it can't figure out how tall
 * the menu items are based on the renderOption if it's not like just text.  You just need to add a height to the renderOption.
 */
export default function MenuSelect({
    label,
    labelProps,
    labelEnhancer,
    moreInfo,
    labelActions,
    containerProps,
    placeholder,
    textModification,
    options,
    renderValue,
    renderOption,
    value,
    setValue = () => {},
    listMaxHeight,
    listHeader,
    listOverrides,
    menuOverrides,
    divideIndices,
    error,
    hideDropdownIcon,
    stackOverrides,
    menuItemOverrides,
    ...props
}: MenuSelectProps) {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const menuListRef = useRef<HTMLDivElement>(null)

    /**
     * Workaround for Chakra UI Menu scroll jitter (chakra-ui/chakra-ui#7325).
     * Chakra calls focus() on MenuItems during hover, which triggers the
     * browser's native scrollIntoView and creates a scroll-focus feedback
     * loop. We patch focus() on each item to use preventScroll, then
     * manually handle keyboard-driven scrolling so arrow keys still work.
     */
    useEffect(() => {
        const el = menuListRef.current
        if (!el || !isOpen) return

        const patchFocus = (node: HTMLElement) => {
            node.focus = function (options?: FocusOptions) {
                HTMLElement.prototype.focus.call(this, {
                    ...options,
                    preventScroll: true,
                })
            }
        }

        patchFocus(el)

        const items = el.querySelectorAll<HTMLElement>('[role="menuitem"]')
        items.forEach(patchFocus)

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                requestAnimationFrame(() => {
                    const focused = el.querySelector(':focus') as HTMLElement
                    focused?.scrollIntoView({ block: 'nearest' })
                })
            }
        }

        el.addEventListener('keydown', handleKeyDown)

        return () => {
            delete (el as any).focus
            items.forEach(item => {
                delete (item as any).focus
            })
            el.removeEventListener('keydown', handleKeyDown)
        }
    }, [isOpen])

    return (
        <Box {...containerProps}>
            {(label || moreInfo || labelActions) && (
                <Box mb={1}>
                    <SharedStack row fit align="center" mb={label ? 1 : 0}>
                        {label && (
                            <SharedLabel fontSize="sm" {...labelProps}>
                                {label}
                            </SharedLabel>
                        )}
                        {labelActions}
                    </SharedStack>
                    {!!moreInfo && moreInfo}
                </Box>
            )}
            <SharedStack row fit alignItems="center" {...stackOverrides}>
                <Menu
                    isOpen={isOpen}
                    onClose={onClose}
                    matchWidth
                    {...menuOverrides}
                >
                    <MenuButton
                        as={Button}
                        variant="outline"
                        rightIcon={
                            hideDropdownIcon ? undefined : <ChevronDownIcon />
                        }
                        textAlign="left"
                        onClick={e => {
                            e.stopPropagation()
                            e.preventDefault()
                            if (props.isDisabled) return
                            isOpen ? onClose() : onOpen()
                        }}
                        {...props}
                    >
                        {renderValue && value ? (
                            renderValue(value)
                        ) : (
                            <HStack spacing={1}>
                                {labelEnhancer}
                                <Text>
                                    {value
                                        ? textModification
                                            ? textModification(value)
                                            : value
                                        : placeholder || 'Select'}
                                </Text>
                            </HStack>
                        )}
                    </MenuButton>
                    <MenuList
                        ref={menuListRef}
                        maxH={listMaxHeight || '296px'}
                        overflowY="auto"
                        color="gray.900"
                        {...listOverrides}
                    >
                        {listHeader && (
                            <>
                                <Box px={4}>{listHeader}</Box>
                                <GradientDivider my={1} />
                            </>
                        )}
                        {options &&
                            options.map((o, index) => {
                                const curVal =
                                    typeof o === 'string' ? o : o?.value
                                const shouldShowDivider =
                                    divideIndices?.includes(index)

                                const menuItem = renderOption ? (
                                    <MenuItem
                                        key={`${o}-${index}`}
                                        icon={
                                            value === curVal
                                                ? FaCheck
                                                : VscBlank
                                        }
                                        onClick={e => {
                                            e.stopPropagation()
                                            e.preventDefault()
                                            setValue(curVal, index)
                                        }}
                                        {...menuItemOverrides}
                                    >
                                        {renderOption(curVal || '', index)}
                                    </MenuItem>
                                ) : (
                                    <MenuItem
                                        key={`${o}-${index}`}
                                        icon={
                                            value === curVal || ''
                                                ? FaCheck
                                                : VscBlank
                                        }
                                        onClick={e => {
                                            e.stopPropagation()
                                            e.preventDefault()
                                            setValue(curVal || '', index)
                                        }}
                                        w="full"
                                        {...menuItemOverrides}
                                    >
                                        <React.Fragment>
                                            {typeof o === 'string'
                                                ? ''
                                                : o?.rightElement}
                                        </React.Fragment>
                                        <React.Fragment>
                                            {curVal && textModification
                                                ? textModification(curVal)
                                                : curVal}
                                        </React.Fragment>
                                    </MenuItem>
                                )

                                return (
                                    <React.Fragment key={`fragment-${index}`}>
                                        {shouldShowDivider && (
                                            <GradientDivider my={1} />
                                        )}
                                        {menuItem}
                                    </React.Fragment>
                                )
                            })}
                    </MenuList>
                </Menu>
                {!!error && (
                    <Popover>
                        <PopoverTrigger>
                            <Box>
                                <WarningIcon boxSize={6} color="yellow.500" />
                            </Box>
                        </PopoverTrigger>
                        <PopoverContent>
                            <PopoverArrow />
                            <PopoverBody>{error}</PopoverBody>
                        </PopoverContent>
                    </Popover>
                )}
            </SharedStack>
        </Box>
    )
}
